import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { InsuranceOrderCardProps } from 'entities/common/my-ticket/model/types'
import { useInsuranceDetailsDialogs } from 'entities/insurance'

import { useTimer } from 'shared/hooks/use-timer'
import { previewPdfUrl } from 'shared/lib/pdf-preview'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Button } from 'shared/ui/button'

import { getInsuranceStatus } from '../../lib/getInsuranceStatus'
import { MyOrderStatus } from '../my-order-status'

import {
  formatInsuranceOrderTimer,
  getEffectiveInsuranceOrderStatus,
} from './lib/insurance-order-card'
import s from './styles.module.scss'

export const InsuranceOrderCard = ({
  title,
  policyNumber,
  participantsCount,
  participants,
  coverage,
  description,
  periodStart,
  periodEnd,
  price,
  pdfUrl,
  orderStatus,
  paymentTtlSeconds,
  onPay,
  onRepeat,
}: InsuranceOrderCardProps) => {
  const { t } = useTranslation()
  const { showProgramDetails, showParticipants } = useInsuranceDetailsDialogs()
  const timer = useTimer(orderStatus === 'CREATED' ? paymentTtlSeconds : 0)
  const timerStarted = useRef(paymentTtlSeconds <= 0)
  const [isExpired, setIsExpired] = useState(paymentTtlSeconds <= 0)

  useEffect(() => {
    timerStarted.current = paymentTtlSeconds <= 0
    setIsExpired(paymentTtlSeconds <= 0)
  }, [orderStatus, paymentTtlSeconds])

  useEffect(() => {
    if (orderStatus !== 'CREATED' || isExpired) return

    if (timer.totalSeconds > 0) {
      timerStarted.current = true
      return
    }

    if (timerStarted.current) setIsExpired(true)
  }, [isExpired, orderStatus, timer.totalSeconds])

  const effectiveOrderStatus = getEffectiveInsuranceOrderStatus(
    orderStatus,
    isExpired,
  )
  const effectiveStatus = getInsuranceStatus(effectiveOrderStatus)
  const isConfirmed = effectiveOrderStatus === 'CONFIRMED'
  const isBooked = effectiveOrderStatus === 'CREATED'
  const isCancelled = effectiveOrderStatus === 'CANCELLED'
  const timerValue = timerStarted.current
    ? timer.totalSeconds
    : paymentTtlSeconds

  return (
    <article className={s.container}>
      <div className={s.statusRow}>
        <MyOrderStatus
          title={t(effectiveStatus.name || 'status_names.pending')}
          color={effectiveStatus.color}
          background={effectiveStatus.background}
        />
        {isBooked && (
          <span className={clsx('semi-bold-12', s.timer)}>
            {formatInsuranceOrderTimer(timerValue)}
          </span>
        )}
      </div>

      <div className={s.primaryData}>
        <p className={clsx('semi-bold-14', s.title)}>{title}</p>

        {policyNumber && (
          <p className={clsx('regular-12', s.row)}>
            <span>{t('my_orders_block.insurance.policy_number')}</span>{' '}
            {policyNumber}
          </p>
        )}
      </div>

      <div className={s.divider} />

      <div className={s.programData}>
        <p className={clsx('regular-12', s.row)}>
          <span>{t('my_orders_block.insurance.program')}</span> {coverage}
        </p>
        {description && (
          <button
            type="button"
            className={clsx('medium-12', s.details)}
            onClick={() => showProgramDetails(coverage, description)}
          >
            {t('insurance.vzr.program.details')}
          </button>
        )}
      </div>

      <div className={s.divider} />

      <div className={s.secondaryData}>
        <p className={clsx('regular-12', s.row)}>
          <span>{t('my_orders_block.insurance.participants')}</span>{' '}
          {participantsCount === undefined
            ? '—'
            : t('my_orders_block.insurance.participants_value', {
                count: participantsCount,
              })}
        </p>
        {participants.length > 0 && (
          <button
            type="button"
            className={clsx('medium-12', s.details)}
            onClick={() => showParticipants(participants)}
          >
            {t('insurance.vzr.program.details')}
          </button>
        )}
      </div>

      <div className={s.divider} />

      <div className={s.secondaryData}>
        <p className={clsx('regular-12', s.row)}>
          <span>{t('my_orders_block.insurance.period')}</span>{' '}
          {t('my_orders_block.insurance.period_value', {
            start: periodStart,
            end: periodEnd,
          })}
        </p>
        <p className={clsx('regular-12', s.row)}>
          <span>{t('my_orders_block.insurance.price')}</span> {price}
        </p>
      </div>

      {isConfirmed && pdfUrl && (
        <>
          <div className={s.divider} />
          <button
            type="button"
            className={clsx('bold-14', s.pdfButton)}
            onClick={() => previewPdfUrl(pdfUrl)}
          >
            {t('my_orders_block.insurance.open_pdf')}
            <AppIcon
              id={AllIcons.CHEVRON_RIGHT}
              width={12}
              height={12}
            />
          </button>
        </>
      )}

      {isBooked && onPay && (
        <Button
          type="button"
          className={clsx('semi-bold-16', s.primaryButton)}
          onClick={onPay}
        >
          {t('my_orders_block.insurance.pay')}
        </Button>
      )}

      {isCancelled && onRepeat && (
        <Button
          type="button"
          className={clsx('semi-bold-16', s.primaryButton)}
          onClick={onRepeat}
        >
          {t('my_orders_block.insurance.repeat_order')}
        </Button>
      )}
    </article>
  )
}
